/**
 * KAN-306: витрина тарифов: выбрать и подключить.
 *
 * Тариф описывается СОСТАВОМ, а не полями базы: клиент выбирает по тому, что
 * входит. KAN-989: "Подключить" списывает абонентку с кошелька кабинета после
 * подтверждения, без перехода на ЮKassa. Сценарий покупки в useTariffPurchaseFlow.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { voiceBillingApi } from '@/services/api/voiceBilling';
import type { TariffPlan } from '@/types/voiceBilling';
import { Check } from 'lucide-react';
import { featureLabel } from '@/lib/features';
import { useTariffPurchaseFlow } from '@/hooks/useTariffPurchase';
import { formatWholeRub } from '@/lib/wallet';
import { TariffConfirmDialog } from './TariffConfirmDialog';

interface Props {
  /** Название действующего тарифа — чтобы отличить его от остальных. */
  currentPlanName?: string;
  /**
   * KAN-342: действует ли ОПЛАЧЕННЫЙ тариф. Пока да, сменить его нельзя —
   * бэкенд отклонит покупку другого. Кнопка обязана это показывать заранее:
   * предлагать то, что заведомо не пройдёт, — обман интерфейса.
   *
   * В пробном периоде флаг ложный: из пробы покупать можно любой тариф.
   */
  paidTariffInForce?: boolean;
  /** До какого числа действует текущий — чтобы объяснить отказ датой. */
  currentEndsAt?: string | null;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function TariffStorefront({ currentPlanName, paidTariffInForce, currentEndsAt }: Props) {
  const purchase = useTariffPurchaseFlow();
  const { pendingPlanId, isPending } = purchase;

  const { data: plans, isLoading } = useQuery({
    queryKey: ['tariff-plans'],
    queryFn: voiceBillingApi.getPlans,
  });

  const handleChoose = (plan: TariffPlan) => purchase.requestPurchase(plan);

  if (isLoading) {
    return <p className="text-sm text-gray-500">Загружаем тарифы…</p>;
  }

  if (!plans || plans.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="tariff-storefront-empty">
        Тарифы пока не настроены.
      </p>
    );
  }

  return (
    <Card data-testid="tariff-storefront">
      <CardHeader>
        <CardTitle className="text-lg">Тарифы</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => {
            const isCurrent = plan.name === currentPlanName;
            return (
              <div
                key={plan.id}
                data-testid={`tariff-card-${plan.id}`}
                className={`flex flex-col gap-3 rounded-lg border p-4 ${
                  isCurrent ? 'border-blue-600 bg-blue-50/40' : 'border-gray-200'
                }`}>
                <div>
                  <div className="font-medium">{plan.name}</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">
                    {formatWholeRub(plan.price_monthly_rub)} ₽
                    <span className="ml-1 text-sm font-normal text-gray-500">/мес</span>
                  </div>
                </div>

                {/* KAN-988: тариф описывается функциями (подписи из lib/features.ts) */}
                <ul className="flex-1 space-y-1 text-sm text-gray-600" data-testid={`tariff-features-${plan.id}`}>
                  {(plan.features ?? []).map((code) => (
                    <li key={code} className="flex items-center gap-2">
                      <Check className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                      <span>{featureLabel(code)}</span>
                    </li>
                  ))}
                  <li>{plan.max_data_sources} источников данных</li>
                  <li>{plan.max_users} сотрудников</li>
                </ul>

                {isCurrent && paidTariffInForce ? (
                  // Свой оплаченный тариф продлить можно всегда: месяц
                  // прибавится к остатку, оплаченное не сгорает.
                  <button
                    type="button"
                    onClick={() => handleChoose(plan)}
                    disabled={isPending}
                    data-testid="tariff-renew"
                    className="rounded-md border border-blue-600 px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-60">
                    {pendingPlanId === plan.id ? 'Продлеваем…' : 'Продлить'}
                  </button>
                ) : isCurrent ? (
                  <span
                    className="rounded-md bg-gray-100 px-3 py-2 text-center text-sm text-gray-600"
                    data-testid="tariff-current">
                    Ваш тариф
                  </span>
                ) : paidTariffInForce ? (
                  // Другой тариф при действующем оплаченном сервер отклонит
                  // (KAN-342). Показываем это ДО нажатия и объясняем, когда
                  // смена станет возможна.
                  <span
                    className="rounded-md bg-gray-100 px-3 py-2 text-center text-xs leading-snug text-gray-500"
                    data-testid="tariff-locked">
                    Доступен после {currentEndsAt ? formatDate(currentEndsAt) : 'окончания текущего тарифа'}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleChoose(plan)}
                    disabled={isPending}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60">
                    {pendingPlanId === plan.id ? 'Подключаем…' : 'Подключить'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {purchase.error && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-700" data-testid="tariff-storefront-error">
              {purchase.error.message}
            </p>
            {purchase.error.insufficientFunds && (
              <Link
                to="/balance"
                data-testid="tariff-topup-link"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white">
                Пополнить баланс
              </Link>
            )}
          </div>
        )}

        {purchase.purchasedPlanName && (
          <p className="mt-3 text-sm text-green-700" data-testid="tariff-storefront-success">
            Тариф {purchase.purchasedPlanName} подключён, абонентка списана с баланса.
          </p>
        )}
      </CardContent>

      <TariffConfirmDialog
        plan={purchase.confirmPlan}
        isPending={isPending}
        onConfirm={purchase.confirmPurchase}
        onCancel={purchase.cancelPurchase}
      />
    </Card>
  );
}
